import { useEffect, useRef, useState } from "react";

/**
 * Shadow a pinned header casts once content scrolls beneath it (Figma
 * 15544-191515). The Figma drop-shadow carries a 3px x-offset that is
 * invisible on a full-width bar but would seam per-cell table headers, so
 * only the vertical offset and blur are kept.
 */
export const STICKY_SHADOW = "0 1px 2.5px rgba(34, 34, 34, 0.14)";

function scrollParent(node: HTMLElement | null): HTMLElement | null {
  for (let el = node?.parentElement; el; el = el.parentElement) {
    // OverlayScrollbars viewports compute as overflow: hidden until content
    // actually overflows, so match them by attribute rather than style.
    if (el.hasAttribute("data-overlayscrollbars-viewport")) return el;
    const { overflowY } = getComputedStyle(el);
    if (overflowY === "auto" || overflowY === "scroll") return el;
  }
  return null;
}

/**
 * Whether the nearest scroll container has scrolled at all — the moment a
 * sticky header inside it has content moving underneath. Attach the ref to
 * the sticky element; no per-callsite wiring beyond that.
 */
export function useScrolledUnder<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    // Scroll doesn't bubble, but a capture listener on window sees every
    // scroller. Re-resolving the container per event also survives
    // OverlayScrollbars re-wrapping the DOM after this effect ran (its host
    // briefly computes as scrollable before init hands scrolling to the
    // inner viewport).
    const update = () => {
      const container = scrollParent(ref.current);
      setScrolled(container !== null && container.scrollTop > 0);
    };
    update();
    window.addEventListener("scroll", update, { capture: true, passive: true });
    return () =>
      window.removeEventListener("scroll", update, { capture: true });
  }, []);

  return { ref, scrolled };
}
